import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserIdleService } from 'angular-user-idle';
import { LiveSignalCacheService } from 'src/app/features/live-tests/services/live-signal-cache.service';
import { DataCacheService } from 'src/app/features/working-area/components/working-area2/services/data/data-cache.service';
import { AuthService } from 'src/app/features/auth/services/auth.service';
import { NotificationService } from './notification.service';
import { SessionStateResetService } from './session/session-state-reset.service';
import { SsoScopeService } from './sso/sso-scope.service';
import { StorageBrowser } from './storage.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class LogoutService {
  constructor(
    private notificationService: NotificationService,
    private dataCacheService: DataCacheService,
    private liveSignalCacheService: LiveSignalCacheService,
    private storageBrowser: StorageBrowser,
    private userService: UserService,
    private router: Router,
    private userIdle: UserIdleService,
    private dialog: MatDialog,
    private ssoScope: SsoScopeService,
    private sessionStateReset: SessionStateResetService,
    private authService: AuthService,
  ) {}

  public logout(message?: string) {
    console.log('[LogoutService] logout()', { message });
    this.tearDownSession();
    this.router.navigate(['auth/login']);
    this.clearAllIndexedDB();

    if (message) {
      this.notificationService.openDialog({
        title: 'Logged Out!',
        message,
        cancelButtonHide: true,
        confirmButtonText: 'OK',
        onConfirm: () => {},
      });
    }
  }

  /**
   * Tear down the session and hand control to a full-page navigation
   * (used for the SSO "Back to main lab" button — the external origin
   * cannot be reached via Angular Router).
   */
  public logoutAndRedirect(externalUrl: string) {
    console.log('[LogoutService] logoutAndRedirect()', { externalUrl });
    this.tearDownSession();
    // Fire-and-forget; the browser navigation will preempt any in-flight
    // async cleanup. IndexedDB cleanup is best-effort here.
    this.clearAllIndexedDB();
    window.location.href = externalUrl;
  }

  /**
   * Tell the server the session is over, then forget it locally.
   *
   * The local teardown below is only half of a logout: the refresh token is an
   * HttpOnly cookie and a database row, and neither is reachable from here.
   * Left alone, the next person to open the app on this machine arrives with
   * no access token, the app does the natural thing and refreshes, and the
   * cookie hands them the previous person's session.
   *
   * Fire-and-forget on purpose, and errors are swallowed. Logging out must
   * finish even when the network does not: leaving somebody on a screen that
   * still looks signed in because a request failed would be worse than the
   * server-side row outliving the tab.
   */
  private endServerSession(): void {
    this.authService.logout().subscribe({
      next: () => undefined,
      error: (err) => console.warn('[LogoutService] server logout failed', err),
    });
  }

  private tearDownSession() {
    this.endServerSession();
    // Storage is cleared below and the browser databases just after. This is the
    // layer between the two: whatever the running services are still holding
    // about this user, which a route change out of the app does not disturb.
    this.sessionStateReset.clearAll();
    this.userIdle.stopWatching();
    this.userService.currentUser.next(null);
    this.storageBrowser.remove('user');
    this.storageBrowser.remove('monitor_token');
    this.storageBrowser.remove('monitor_token_time');
    this.storageBrowser.remove('token');
    this.storageBrowser.remove('last_active');
    this.ssoScope.clear();
    this.dialog.closeAll();
  }

  private async clearAllIndexedDB() {
    try {
      await Promise.all([
        this.dataCacheService.clear(),
        this.liveSignalCacheService.clearAll(),
      ]);
      console.log('[LogoutService] local db is cleared');
    } catch (error) {
      console.log('[LogoutService] error clearing local db');
      console.error({ error });
    }
  }
}
